import { useState, useEffect, useCallback, useRef } from 'react';
import { getInterviewsByCompanyId, getInterviewsByJobId, PaginationParams } from '../api/ApplicationService';
import { IInterview } from '../types/interview';
import { FormBuilderService } from '../api/FormBuilderService';
import { IQuestionnaire } from '../types/questionnaire';
import config from '../data/config';
import { setWithExpiry, getWithExpiry, removeItem } from '../utils/sessionStorage';
import { getDateRangeBounds } from '../utils/dateRange';

export interface EnrichedInterviewData extends IInterview {
  questionnaire?: IQuestionnaire;
  mainThumbnail?: string;
  jobTitle?: string;
}

interface CachedInterviewData {
  interviews: EnrichedInterviewData[];
  totalInterviews: number;
  currentPage: number;
  hasLoadedAll: boolean;
  timestamp: number;
}

// VI-753: the default DatePicker range. Must match usePersistedInterviewState's
// default so the hook's initial (self-triggered) load and the page's selected
// range agree, avoiding a redundant refetch on mount.
const DEFAULT_DATE_RANGE = 'Last 30 Days';

// VI-753: JobApplication API caps a single page at 100. We fill the full
// date-bounded set with ceil(total / PAGE_LIMIT) requests.
const PAGE_LIMIT = 100;

// VI-753: safety cap on how large a set we cache in sessionStorage (enriched
// records carry responses/thumbnails; a huge "All"-range set could exceed quota).
const MAX_CACHEABLE = 500;

// Helper function to deduplicate and sort interviews
const deduplicateAndSortInterviews = (interviews: EnrichedInterviewData[]): EnrichedInterviewData[] => {
  // Use Map for O(1) lookup - key by applicationGuid for uniqueness
  const uniqueMap = new Map<string, EnrichedInterviewData>();

  interviews.forEach(interview => {
    const key = interview.applicationGuid;
    // Keep the more recent version if duplicate exists
    const existing = uniqueMap.get(key);
    if (!existing || new Date(interview.createdAt) > new Date(existing.createdAt)) {
      uniqueMap.set(key, interview);
    }
  });

  // Convert back to array and sort by createdAt descending (newest first)
  return Array.from(uniqueMap.values()).sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
};

export const useInterviewManagement = (
  companyId: string | number,
  initialDateRange: string = DEFAULT_DATE_RANGE
) => {
  const [interviews, setInterviews] = useState<EnrichedInterviewData[]>([]);
  const [questionnaires, setQuestionnaires] = useState<IQuestionnaire[]>([]);
  const [isLoadingInterviews, setIsLoadingInterviews] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingQuestionnaires, setIsLoadingQuestionnaires] = useState(false);
  const [isLoadingByJobId, setIsLoadingByJobId] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // VI-623: the jobId currently being viewed (null = company-wide). Reactive
  // (unlike isViewingJobInterviewsRef) so response-count scope follows the grid
  // even when selectedJob UI state isn't in sync (e.g. Jobs-tab count click).
  const [viewingJobId, setViewingJobId] = useState<number | null>(null);

  // Prevent duplicate calls
  const isInitializedRef = useRef(false);
  const hasCheckedCacheRef = useRef(false); // Add this to track cache check

  // VI-753: the currently selected DatePicker range, plumbed down from
  // InterviewsPage via refetchForDateRange. The company list is fetched
  // server-side, date-bounded to this range (same bounds the counts badge uses),
  // so the grid and the badge can never disagree.
  const dateRangeRef = useRef<string>(initialDateRange);

  // VI-753: monotonic token so a range change (or a new fetch) supersedes any
  // in-flight fill loop — later pages of a stale range are discarded instead of
  // racing into state.
  const fetchTokenRef = useRef(0);

  // 🔥 NEW: Ref to preserve company-wide interviews (survives job filter changes)
  // Using ref instead of state to avoid unnecessary re-renders
  const companyInterviewsRef = useRef<EnrichedInterviewData[]>([]);

  // Track if we're currently viewing job-specific interviews
  const isViewingJobInterviewsRef = useRef(false);

  // Store company pagination state in refs (preserved across job filter changes)
  const companyPaginationRef = useRef({
    currentPage: 1,
    totalInterviews: 0,
    hasLoadedAll: false
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalInterviews, setTotalInterviews] = useState(0);
  const [itemsPerPage] = useState(20);
  const [hasLoadedAll, setHasLoadedAll] = useState(false);

  // FEATURE FLAG: if companyId is 0, we disable the interview feature
  const isDisabled = !companyId || companyId === 0;

  // VI-753: cache key now includes the date range so different ranges don't
  // clobber each other (stale cross-range data was a root enabler of VI-749).
  // Mirrors useResponseCounts' `responseCounts_${companyId}_${jobId}_${dateRange}`.
  const getCacheKey = useCallback((range: string, status?: string) => {
    return `interviews_${companyId}_${range}_${status || 'all'}`;
  }, [companyId]);

  // Stable fetchQuestionnaires function
  const fetchQuestionnaires = useCallback(async () => {
    if (!companyId || isLoadingQuestionnaires || isDisabled) return;

    console.log('📋 Fetching questionnaires for company:', companyId);
    setIsLoadingQuestionnaires(true);
    try {
      const questionnaireData = await FormBuilderService.getQuestionnairesByCompanyId(companyId.toString());
      setQuestionnaires(questionnaireData);
      console.log(`✅ Loaded ${questionnaireData.length} questionnaires`);
    } catch (err) {
      console.error('Error fetching questionnaires:', err);
      setError('Failed to load questionnaires');
    } finally {
      setIsLoadingQuestionnaires(false);
    }
  }, [companyId, isLoadingQuestionnaires, isDisabled]);

  // Enrich a raw interview with its questionnaire + a display thumbnail.
  const enrichInterview = useCallback((interview: IInterview): EnrichedInterviewData => {
    const questionnaire = questionnaires.find(q => q.questionnaireId === interview.questionnaireId);
    const isAudio = (interview.contentType || '').toLowerCase() === 'audio';
    const mainThumbnail = (!isAudio && interview.responses?.[0]?.answer?.thumbnail) || '/images/audio-placeholder.jpg';
    return {
      ...interview,
      questionnaire,
      jobTitle: interview.jobTitle,
      mainThumbnail
    };
  }, [questionnaires]);

  // VI-753: fetch the FULL date-bounded Completed set for the current range.
  //
  // Replaces the old page-by-page auto-loader (VI-749 root cause): the grid used
  // to fetch raw pages with no server date filter and page + date-filter in the
  // browser, and a mis-ordered auto-load could skip page 1 (the newest). Now the
  // server bounds by date (identical bounds to the counts badge) and returns an
  // authoritative `total`; we page (limit 100) only until `loaded >= total`, so
  // the grid always matches the badge and the newest page can never be skipped.
  //
  // The page/status/append positional args are retained for call-site
  // compatibility; `page`/`append` are ignored (we always load from page 1 to
  // completion). A `statusOverride` still wins over the default 'Completed'.
  const fetchInterviews = useCallback(async (
    _page: number = 1,
    statusOverride?: string,
    _append: boolean = false
  ) => {
    if (!companyId || questionnaires.length === 0 || isDisabled) return;

    // Company-wide fetch → response counts should be company-scoped.
    setViewingJobId(null);

    const range = dateRangeRef.current;
    const status = (statusOverride && statusOverride !== 'all') ? statusOverride : 'Completed';
    const { startDate, endDate } = getDateRangeBounds(range);

    // CHECK FOR EXPANDED INTERVIEW CACHE FIRST (only on initial load, same range)
    if (!hasCheckedCacheRef.current) {
      const expandedCache = getWithExpiry('expandedInterviewData');
      const cachedRange = expandedCache?.filters?.selectedDateRange;
      if (
        expandedCache?.interviewDataState?.allInterviews?.length > 0 &&
        cachedRange === range
      ) {
        console.log(`🔄 Restoring interview data from expanded cache: ${expandedCache.interviewDataState.allInterviews.length} interviews`);
        const restoredInterviews: EnrichedInterviewData[] = expandedCache.interviewDataState.allInterviews;
        setInterviews(restoredInterviews);
        setTotalInterviews(expandedCache.interviewDataState.totalInterviews);
        setCurrentPage(1);
        setHasLoadedAll(true);
        setIsLoadingInterviews(false);
        setIsLoadingMore(false);
        hasCheckedCacheRef.current = true;
        companyInterviewsRef.current = restoredInterviews;
        companyPaginationRef.current = {
          currentPage: 1,
          totalInterviews: expandedCache.interviewDataState.totalInterviews,
          hasLoadedAll: true
        };
        isViewingJobInterviewsRef.current = false;
        removeItem('expandedInterviewData');
        return;
      }
      // VI-753: a mismatched-range (or empty) expanded snapshot must not seed the
      // grid with a different range's data — drop it and fetch fresh.
      if (expandedCache) removeItem('expandedInterviewData');
    }

    // 📦 SESSION CACHE (full range-bounded set, 5-minute TTL)
    const cacheKey = getCacheKey(range, status);
    const cachedData = getWithExpiry(cacheKey) as CachedInterviewData | null;
    if (cachedData?.interviews && cachedData.interviews.length > 0) {
      console.log(`💾 Using cached data (${range}): ${cachedData.interviews.length} interviews`);
      setInterviews(cachedData.interviews);
      setTotalInterviews(cachedData.totalInterviews);
      setCurrentPage(1);
      setHasLoadedAll(true);
      setIsLoadingInterviews(false);
      setIsLoadingMore(false);
      hasCheckedCacheRef.current = true;
      companyInterviewsRef.current = cachedData.interviews;
      companyPaginationRef.current = {
        currentPage: 1,
        totalInterviews: cachedData.totalInterviews,
        hasLoadedAll: true
      };
      isViewingJobInterviewsRef.current = false;
      return;
    }

    hasCheckedCacheRef.current = true;

    const myToken = ++fetchTokenRef.current;
    const overallStartTime = Date.now();

    setIsLoadingInterviews(true);
    setInterviews([]);
    setCurrentPage(1);
    setHasLoadedAll(false);
    setError(null);

    try {
      // Page 1 (newest). Render it immediately, then fill the rest in the
      // background bounded by the authoritative server total.
      const firstParams: PaginationParams = { page: 1, limit: PAGE_LIMIT, status: status as any, startDate, endDate };
      const first = await getInterviewsByCompanyId(companyId, firstParams);
      if (fetchTokenRef.current !== myToken) return; // superseded by a newer fetch

      const total = first.total || 0;
      let accumulated = deduplicateAndSortInterviews((first.data || []).map(enrichInterview));
      setInterviews(accumulated);
      setTotalInterviews(total);
      isViewingJobInterviewsRef.current = false;
      setIsLoadingInterviews(false);

      const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
      if (accumulated.length < total && totalPages > 1) {
        setIsLoadingMore(true);
        try {
          for (let page = 2; page <= totalPages; page++) {
            const res = await getInterviewsByCompanyId(companyId, {
              page,
              limit: PAGE_LIMIT,
              status: status as any,
              startDate,
              endDate
            });
            if (fetchTokenRef.current !== myToken) return; // superseded
            if (!res.data || res.data.length === 0) break;
            accumulated = deduplicateAndSortInterviews([...accumulated, ...res.data.map(enrichInterview)]);
            setInterviews(accumulated);
          }
        } finally {
          if (fetchTokenRef.current === myToken) setIsLoadingMore(false);
        }
      }

      if (fetchTokenRef.current !== myToken) return;

      setHasLoadedAll(true);
      setTotalInterviews(total);
      companyInterviewsRef.current = accumulated;
      companyPaginationRef.current = { currentPage: 1, totalInterviews: total, hasLoadedAll: true };
      isViewingJobInterviewsRef.current = false;

      // 💾 CACHE THE FULL SET (5 minutes). Guarded for quota; skip very large sets.
      if (accumulated.length > 0 && accumulated.length <= MAX_CACHEABLE) {
        try {
          const cacheData: CachedInterviewData = {
            interviews: accumulated,
            totalInterviews: total,
            currentPage: 1,
            hasLoadedAll: true,
            timestamp: Date.now()
          };
          setWithExpiry(cacheKey, cacheData, 5 * 60 * 1000);
        } catch (e) {
          console.warn('Skipping interviews cache (storage quota)', e);
        }
      }

      console.log(`🚀 Loaded ${accumulated.length}/${total} interviews (${range}) in ${Date.now() - overallStartTime}ms`);
    } catch (err: any) {
      if (fetchTokenRef.current !== myToken) return;
      console.error('Error fetching interviews:', err);
      setError('Failed to load interviews');
    } finally {
      if (fetchTokenRef.current === myToken) {
        setIsLoadingInterviews(false);
        setIsLoadingMore(false);
      }
    }
  }, [companyId, questionnaires.length, isDisabled, getCacheKey, enrichInterview]);

  // VI-753: clear every cached interviews entry for this company (all ranges +
  // job keys) by prefix, so a range switch / manual refresh never serves stale
  // cross-range data.
  const clearInterviewsCache = useCallback(() => {
    const prefixes = [`interviews_${companyId}_`, `interviews_job_${companyId}_`];
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && prefixes.some(p => key.startsWith(p))) {
          sessionStorage.removeItem(key);
        }
      }
    } catch (e) {
      console.warn('Failed to clear interviews cache', e);
    }
    hasCheckedCacheRef.current = false; // Reset cache check flag
    console.log(`🧹 Cleared interviews cache for company ${companyId}`);
  }, [companyId]);

  // VI-753: called by InterviewsPage when the DatePicker range changes. Updates
  // the range, cancels any in-flight fill, clears the cache, and refetches the
  // full date-bounded set. When a job is selected the grid is client-filtered by
  // date (the job endpoint isn't date-bounded), so no server refetch is needed —
  // we just record the new range for when the job filter is later cleared.
  const refetchForDateRange = useCallback((range: string) => {
    dateRangeRef.current = range;
    if (isViewingJobInterviewsRef.current) {
      return;
    }
    fetchTokenRef.current++; // cancel any in-flight company fill
    clearInterviewsCache();
    hasCheckedCacheRef.current = false;
    fetchInterviews();
  }, [fetchInterviews, clearInterviewsCache]);

  // Prevent duplicate initialization
  useEffect(() => {
    if(isDisabled) return;

    if (companyId && !isInitializedRef.current) {
      console.log('🚀 Initializing FAST interview management for company:', companyId);
      isInitializedRef.current = true;
      hasCheckedCacheRef.current = false; // Reset cache check flag
      fetchQuestionnaires();
    }
  }, [companyId, fetchQuestionnaires, isDisabled]);

  // UPDATED: This effect now triggers cache check immediately when questionnaires are ready
  useEffect(() => {
    if(isDisabled) return;

    // Try to load from cache or fetch interviews when questionnaires are ready
    if (questionnaires.length > 0 && interviews.length === 0 && !hasCheckedCacheRef.current) {
      console.log('📋 Questionnaires loaded, checking cache immediately');
      fetchInterviews(1);
    }
  }, [questionnaires.length, fetchInterviews, isDisabled, interviews.length]);

  // ADDED: Effect to handle browser navigation (back/forward)
  useEffect(() => {
    if (isDisabled) return;

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Page was loaded from back/forward cache
        console.log('🔄 Page loaded from cache (back/forward navigation)');
        hasCheckedCacheRef.current = false; // Reset cache check flag

        // If we have questionnaires but no interviews, check cache
        if (questionnaires.length > 0 && interviews.length === 0) {
          console.log('🔍 Checking cache after browser navigation');
          fetchInterviews(1);
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [questionnaires.length, interviews.length, fetchInterviews, isDisabled]);

  //  Method to fetch interviews specifically for a job
  // 🔥 OPTIMIZED: Does NOT touch companyInterviewsRef - preserves company data
  //
  // VI-753 note: the job endpoint (/api/v1/job/:jobId/interviews) is NOT
  // date-bounded server-side (AS-8720 only covered the company list + counts), so
  // this still fetches the newest 100 completed for the job all-time. InterviewsPage
  // then applies a client-side date filter over this set for the job view, keeping
  // grid == badge for job scope without depending on unverified backend behavior.
  // Known limitation: a single job with >100 completed in a range can truncate.
  const fetchInterviewsByJobId = useCallback(async (jobId: number) => {
    if (!companyId || questionnaires.length === 0 || isDisabled || isLoadingByJobId) return;

    console.log(`🔍 Fetching interviews for job ID: ${jobId}`);
    console.log(`📦 Company interviews preserved in ref: ${companyInterviewsRef.current.length}`);

    // Viewing a single job → response counts should be scoped to that job,
    // regardless of whether selectedJob UI state synced (VI-623).
    setViewingJobId(jobId);

    // VI-623: 5-min sessionStorage cache for job-specific results, mirroring the
    // company list cache — avoids a refetch when re-selecting the same job.
    const jobCacheKey = `interviews_job_${companyId}_${jobId}`;
    const cachedJob = getWithExpiry(jobCacheKey) as
      | { interviews: EnrichedInterviewData[]; total: number }
      | null;
    if (cachedJob && Array.isArray(cachedJob.interviews)) {
      console.log(`💾 Using cached job interviews for ${jobId}: ${cachedJob.interviews.length}`);
      setInterviews(cachedJob.interviews);
      setTotalInterviews(cachedJob.total);
      setCurrentPage(1);
      setHasLoadedAll(true);
      isViewingJobInterviewsRef.current = true;
      return;
    }

    setIsLoadingByJobId(true);
    setError(null);

    try {
      const params: PaginationParams = {
        page: 1,
        limit: 100, // Fetch all interviews for this job
        status: 'Completed' // VI-623: Responses grid shows completed submissions only
      };

      const result = await getInterviewsByJobId(jobId, params);

      if (!result.data || result.data.length === 0) {
        console.log(`ℹ️ No interviews found for job ${jobId}`);
        // 🔥 Set empty interviews for this job (clear previous job-specific data)
        setInterviews([]);
        setTotalInterviews(0);
        setIsLoadingByJobId(false);
        isViewingJobInterviewsRef.current = true; // Still mark as job view
        setWithExpiry(jobCacheKey, { interviews: [], total: 0 }, 5 * 60 * 1000);
        return;
      }

      console.log(`✅ Fetched ${result.data.length} interviews for job ${jobId}`);

      // Enrich interviews with questionnaire and thumbnail data
      const enrichedInterviews: EnrichedInterviewData[] = result.data.map(enrichInterview);

      // 🔥 IMPORTANT: Only update STATE, NOT the companyInterviewsRef
      // This preserves company-wide data while showing job-specific view
      setInterviews(enrichedInterviews);
      setTotalInterviews(result.total || enrichedInterviews.length);
      setCurrentPage(1);
      setHasLoadedAll(true);

      // Mark that we're now viewing job-specific interviews
      isViewingJobInterviewsRef.current = true;

      setWithExpiry(
        jobCacheKey,
        { interviews: enrichedInterviews, total: result.total || enrichedInterviews.length },
        5 * 60 * 1000
      );

      console.log(`✅ Loaded ${enrichedInterviews.length} job-specific interviews (company data preserved: ${companyInterviewsRef.current.length})`);
    } catch (err: any) {
      console.error(`Error fetching interviews for job ${jobId}:`, err);
      setError(err.message || 'Failed to load interviews for this job');
    } finally {
      setIsLoadingByJobId(false);
    }
  }, [companyId, questionnaires.length, isDisabled, isLoadingByJobId, enrichInterview]);

  // Method to restore company-wide interviews (used when clearing job filter)
  // 🔥 OPTIMIZED: NO API CALL - uses preserved company data from ref
  const restoreCompanyInterviews = useCallback(() => {
    console.log('🔄 Restoring company-wide interviews (NO API call)...');
    console.log(`📦 Company ref has: ${companyInterviewsRef.current.length} interviews`);
    console.log(`📦 Current state has: ${interviews.length} interviews`);

    // If we were viewing job-specific interviews, merge them into company data
    if (isViewingJobInterviewsRef.current && interviews.length > 0) {
      console.log('🔀 Merging job-specific interviews into company data...');

      // Merge current (job-specific) interviews with company ref data
      const mergedInterviews = deduplicateAndSortInterviews([
        ...companyInterviewsRef.current,
        ...interviews
      ]);

      // Update both ref and state
      companyInterviewsRef.current = mergedInterviews;
      setInterviews(mergedInterviews);

      console.log(`✅ Merged result: ${mergedInterviews.length} unique interviews`);
    } else if (companyInterviewsRef.current.length > 0) {
      // No job-specific interviews to merge, just restore from ref
      console.log('📦 Restoring from company ref (no merge needed)...');
      setInterviews(companyInterviewsRef.current);
    } else {
      // Ref is empty (edge case: first load was directly to job view)
      // In this case, we need to fetch company interviews
      console.log('⚠️ Company ref is empty, fetching from API...');
      fetchInterviews(1, undefined, false);
      return;
    }

    // Restore company pagination state from ref
    setCurrentPage(companyPaginationRef.current.currentPage);
    setTotalInterviews(companyPaginationRef.current.totalInterviews);
    setHasLoadedAll(companyPaginationRef.current.hasLoadedAll);

    // Mark that we're now viewing company interviews
    isViewingJobInterviewsRef.current = false;
    setViewingJobId(null);

    console.log(`✅ Restored company view: ${companyInterviewsRef.current.length} interviews, page ${companyPaginationRef.current.currentPage}`);
  }, [interviews, fetchInterviews]);

  return {
    interviews,
    questionnaires,
    isLoadingInterviews,
    isLoadingMore,
    isLoadingQuestionnaires,
    isLoadingByJobId,
    isLoading: isLoadingInterviews || isLoadingQuestionnaires || isLoadingByJobId,
    error,
    currentPage,
    totalInterviews,
    itemsPerPage,
    hasLoadedAll,
    // VI-753: the full date-bounded set is always loaded to completion, so there
    // is never "more to load". Kept in the return (as a constant) so the existing
    // VideoGrid `hasLoadedAll={!hasMoreToLoad}` wiring stays correct.
    hasMoreToLoad: false,
    totalPages: Math.ceil(totalInterviews / itemsPerPage),
    hasNextPage: false,
    hasPreviousPage: false,
    // VI-753: the background page-by-page loader is gone (it was the VI-749 root
    // cause). These remain as no-ops for prop compatibility with InterviewsPage.
    loadMoreInterviews: useCallback(() => {}, []),
    loadNextPage: useCallback(() => {}, []),
    loadPreviousPage: useCallback(() => {}, []),
    // VI-753: new — refetch the full set for a changed DatePicker range.
    refetchForDateRange,
    filterByStatus: useCallback((status: string) => {
      clearInterviewsCache(); // Clear cache when filtering
      fetchInterviews(1, status, false);
    }, [fetchInterviews, clearInterviewsCache]),
    refetchInterviews: useCallback(() => {
      clearInterviewsCache(); // Clear cache when manually refreshing
      fetchInterviews(1);
    }, [fetchInterviews, clearInterviewsCache]),
    refetchQuestionnaires: fetchQuestionnaires,
    clearInterviewsCache, // Expose for manual cache clearing
    fetchInterviewsByJobId, // Expose method to fetch by job ID
    restoreCompanyInterviews, // Expose method to restore company-wide interviews
    // 🔥 NEW: Expose helpful info about the current state
    companyInterviewsCount: companyInterviewsRef.current.length, // For debugging/display
    isViewingJobSpecific: isViewingJobInterviewsRef.current, // To know if we're in job-filtered view
    viewingJobId // VI-623: jobId currently in view (null = company-wide) for response-count scope
  };
};
